/** Rolagem do container de leitura — o pouco que é imperativo, isolado aqui.
 *
 * Fica fora do componente de propósito. As regras do React Compiler tratam
 * escrita em valor obtido no corpo de um componente como mutação proibida, e
 * escrever `scrollLeft` num nó do DOM é justamente o que rolar significa —
 * silenciar a regra no componente esconderia os casos em que ela está certa.
 */

/** Rola o elemento para a posição pedida.
 *
 * ⚠️ `scrollTo` com objeto de opções não existe em todo lugar (nem no jsdom).
 * Atribuir `scrollLeft`/`scrollTop` funciona em qualquer navegador, e uma
 * exceção aqui derrubaria a página inteira por causa de uma rolagem.
 */
export function rolarPara(
  elemento: Element,
  destino: { left: number; top: number },
): void {
  if (typeof elemento.scrollTo === 'function') {
    elemento.scrollTo({ left: destino.left, top: destino.top })
    return
  }
  elemento.scrollLeft = destino.left
  elemento.scrollTop = destino.top
}

/** Passo da quantização do `scroll_pct`.
 *
 * ⚠️ Não é enfeite. O `useProgress` compara a posição com
 * `scrollPct.toFixed(2)` para decidir se houve mudança: um valor contínuo faria
 * a assinatura mudar a cada pixel rolado e derrubaria a propriedade "aluno
 * parado não gera tráfego", que tem teste dedicado — e o rate limit de
 * progresso é de 30/min por aluno (RN-27b).
 *
 * 5% de uma página A4 é ~1,5cm: a retomada erra menos que a altura de um
 * parágrafo, o que é imperceptível para quem volta à leitura.
 */
export const PASSO_SCROLL = 0.05

export function quantizar(pct: number, passo: number = PASSO_SCROLL): number {
  if (!Number.isFinite(pct)) return 0
  const limitado = Math.min(1, Math.max(0, pct))
  return Number((Math.round(limitado / passo) * passo).toFixed(4))
}

/** Fração já rolada do container (0..1).
 *
 * Divide pelo que é ROLÁVEL (`scrollHeight - clientHeight`), não pela altura
 * total: dividir pela total nunca chegaria a 1, e o fim do livro ficaria
 * marcado como 85% lido para sempre.
 */
export function pctDaRolagem(
  scrollTop: number,
  scrollHeight: number,
  clientHeight: number,
): number {
  const rolavel = scrollHeight - clientHeight
  if (rolavel <= 0) return 0
  return Math.min(1, Math.max(0, scrollTop / rolavel))
}

/** Posição em px que corresponde a uma fração — o inverso de `pctDaRolagem`. */
export function topoParaPct(
  pct: number,
  scrollHeight: number,
  clientHeight: number,
): number {
  const rolavel = Math.max(0, scrollHeight - clientHeight)
  return Math.round(Math.min(1, Math.max(0, pct)) * rolavel)
}
